import React, { useCallback, useMemo, useState, useRef } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import {
  VehiclePaintPointsProps,
  MeasurementArea,
} from './VehiclePaintPoints.types';
import { VehicleView } from './VehicleView';
import {
  getPaintStatusFromValue,
  paintStatusConfig,
  formatMeasurementRange,
} from '../VehiclePaintMeasurement/paintStatusConfig';
import { getVehiclePaintPointsMap } from './paintPointsMapping';
import { PaintThicknessLegend } from '../PaintThicknessLegend';
import {
  MeasurementsMobileGrid,
  MeasurementsMobileGridItem,
} from '../shared/MeasurementsMobileGrid';
import { FixedElementBadge } from '../shared/FixedElementBadge';
import { SliderNavigation } from '../VehiclePaintMeasurement/SliderNavigation';
import useWindowSize from '../../hooks/use-window-size';
import { useMobileStickyNavigation } from '@/hooks/useMobileStickyNavigation';

const NARROW_MOBILE_BREAKPOINT = 440;
const TOP_VIEW_HEIGHT_MOBILE = 450;
const TOP_VIEW_HEIGHT_NARROW_MOBILE = 360;

// TODO(tymczasowe): ukryty widok komory silnika ("Pomiary komory silnika",
// punkty 28–32) na desktopie i mobile. Ustaw na `true`, aby przywrócić widok.
const SHOW_ENGINE_BAY_VIEW = false;

// Helper: oblicza parametry grida z sub-kolumnami (LCM) dla równego wypełnienia ostatniego wiersza
function getGridParams(itemCount: number, maxCols: number, cellWidth: number) {
  const cols = Math.min(itemCount, maxCols);
  const itemsInLastRow = itemCount % cols || cols;
  const isPartial = itemsInLastRow < cols;
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const lcm = (a: number, b: number) => (a * b) / gcd(a, b);
  const subCols = isPartial ? lcm(cols, itemsInLastRow) : cols;
  const subCellWidth = (cols * cellWidth) / subCols;
  const normalSpan = subCols / cols;
  const lastRowSpan = isPartial ? subCols / itemsInLastRow : 1;
  return {
    cols,
    subCols,
    subCellWidth,
    normalSpan,
    lastRowSpan,
    itemsInLastRow,
  };
}

/**
 * Komponent wizualizacji punktów pomiarowych lakieru we wnękach i komorze silnika
 *
 * Wyświetla 3 widoki pojazdu (lewy, górny, prawy) z numerowanymi etykietami
 * punktów pomiarowych oraz legendę statusów i tabelę szczegółów.
 *
 * @example
 * ```tsx
 * <VehiclePaintPoints
 *   vehicleType="sedan"
 *   paintPoints={[
 *     { pointNumber: '12', value: 45, view: 'left' },
 *     { pointNumber: '13', value: 180, view: 'left' },
 *     { pointNumber: '28', value: 420, view: 'top' },
 *   ]}
 * />
 * ```
 */
export const VehiclePaintPoints: React.FC<VehiclePaintPointsProps> = ({
  vehicleType,
  measurementAreas,
  className,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    isActive: isMobileNavigationActive,
    stickyTop: mobileNavigationStickyTop,
  } = useMobileStickyNavigation(sectionRef);
  const { windowSize } = useWindowSize();
  const isNarrowMobile =
    typeof windowSize.width === 'number' &&
    windowSize.width < NARROW_MOBILE_BREAKPOINT;
  const topViewHeightMobile = isNarrowMobile
    ? TOP_VIEW_HEIGHT_NARROW_MOBILE
    : TOP_VIEW_HEIGHT_MOBILE;

  const updateSwiperLayout = useCallback(() => {
    const swiper = swiperRef.current;

    if (!swiper) {
      return;
    }

    swiper.update();
    swiper.updateAutoHeight(0);
  }, []);

  // State dla podświetlania punktów (hover/select w gridach i na SVG)
  const [hoveredPoint, setHoveredPoint] = useState<string | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<string | null>(null);
  const highlightedPoint = hoveredPoint ?? selectedPoint;

  const handlePointClick = useCallback((nameId: string) => {
    setSelectedPoint((prev) => (prev === nameId ? null : nameId));
  }, []);

  const handlePointHover = useCallback((nameId: string | null) => {
    setHoveredPoint(nameId);
  }, []);

  // Grupowanie punktów po widokach - użyj paintPointsMapping żeby określić co pokazać
  const groupedPoints = useMemo(() => {
    const groups: {
      left: MeasurementArea[];
      top: MeasurementArea[];
      right: MeasurementArea[];
    } = {
      left: [],
      top: [],
      right: [],
    };

    if (!measurementAreas) return groups;

    // Pobierz mapę punktów dla danego typu pojazdu
    const pointsMappingForType = getVehiclePaintPointsMap(vehicleType);

    // Iteruj po wszystkich widokach i ich kluczach z paintPointsMapping
    (['left', 'top', 'right'] as const).forEach((view) => {
      const viewMapping = pointsMappingForType[view];

      // Używamy Object.entries() aby mieć pewność że mamy dostęp do kluczy i wartości
      const entries = Object.entries(viewMapping);

      entries.forEach(([pointNumberKey, coordinates]) => {
        // Pomijamy jeśli nie ma współrzędnych
        if (!coordinates) {
          return;
        }

        // Normalizuj klucz do stringa dla porównania
        const normalizedKey = String(pointNumberKey);

        // Znajdź dane dla tego nameId w measurementAreas
        // Porównujemy znormalizowane wartości (oba jako stringi)
        const measurement = measurementAreas.find((m) => {
          const normalizedNameId = String(m.nameId);
          return normalizedNameId === normalizedKey;
        });

        if (measurement) {
          groups[view].push({
            ...measurement,
            nameId: normalizedKey,
            isFixed: measurement.isFixed,
            isMeasured: measurement.isMeasured,
            status: measurement.status,
            value: measurement.value,
            displayValue: coordinates.displayValue,
          });
        }
      });
      // Object.keys(pointsMap[view]).forEach((pointNumber) => {
      //   // Znajdź dane dla tego pointNumber/nameId
      //   const measurement = dataSource.find((m) =>
      //     'nameId' in m ? m.nameId === pointNumber : String(m.pointNumber) === pointNumber
      //   );

      //   if (measurement && pointsMap[view][pointNumber]) {
      //     groups[view].push({
      //       pointNumber: 'nameId' in measurement ? measurement.nameId : measurement.pointNumber,
      //       value: measurement.isMeasured ? measurement.value : undefined,
      //       isFixed: measurement.isFixed,
      //       isMeasured: measurement.isMeasured,
      //     });
      //   }
      // });
    });

    return groups;
  }, [measurementAreas, vehicleType]);

  return (
    <div
      ref={sectionRef}
      className={clsx('relative w-full', className)}
      data-mobile-sticky-navigation-section="true"
    >
      <div
        aria-hidden={!isMobileNavigationActive}
        aria-label="Nawigacja widoków pojazdu"
        data-testid="paint-points-mobile-slider-navigation"
        role="navigation"
        style={{ top: mobileNavigationStickyTop }}
        className={clsx(
          'sticky z-30 h-[156px] transition-[top] duration-150 ease-out motion-reduce:transition-none bg-gradient-to-t from-[#0B2460] via-[#0B2460]/80 to-transparent lg:hidden',
          !isMobileNavigationActive && 'pointer-events-none invisible',
        )}
      >
        <SliderNavigation
          activeIndex={activeSlide}
          visibleSlides={SHOW_ENGINE_BAY_VIEW ? undefined : [0, 2]}
          onNavigate={(index) => {
            swiperRef.current?.slideTo(index);
          }}
        />
      </div>
      {/* Nagłówek sekcji */}
      {/* <SectionSubtitle
        title="Pomiary lakieru wnęk oraz komory silnika"
        description="Wartość zmierzona w [μm]"
      /> */}

      {/* Widoki pojazdu */}
      <div
        className="relative -mt-[156px] overflow-hidden p-8 lg:mt-0 print:overflow-visible"
        style={{
          background: 'linear-gradient(to top, #0B2460 0%, #111438 100%)',
        }}
      >
        <div className="mb-8 text-[18px] font-bold leading-[18px] text-white lg:text-[26px]">
          Pomiary lakieru wnęk{' '}
        </div>{' '}
        {/* oraz komory silnika */}
        {/* Tło SVG Grid - inline SVG (identyczny jak w VehiclePaintMeasurement) */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 40 0 V 40 H 0' fill='none' stroke='rgba(255,255,255,0.03)'/%3E%3C/svg%3E")`,
            backgroundSize: '40px 40px',
          }}
        />
        {/* Widoki pojazdu */}
        <div className="-mx-8 flex max-w-[1268px] flex-col-reverse lg:mx-0 lg:flex-row">
          {/* Desktop: Grid layout */}
          <div
            className={clsx(
              'relative z-10 hidden flex-1 lg:grid',
              SHOW_ENGINE_BAY_VIEW ? 'grid-cols-3' : 'grid-cols-2',
            )}
          >
            <div className="flex flex-col items-start">
              <VehicleView
                view="left"
                vehicleType={vehicleType}
                points={groupedPoints.left}
                label="Strona lewa"
                highlightedPoint={highlightedPoint}
                onPointHover={handlePointHover}
                onPointClick={handlePointClick}
              />
              <div className="lg:ml-[7%]">
                <div className="mb-2 mt-20 text-[18px] font-semibold leading-[20px] text-white">
                  Pomiary lakieru wnęk
                </div>
                {/* oraz komory silnika */}
                <div className="font-regular mb-8 text-[14px] leading-[14px] text-blue-300">
                  Wartość zmierzona w [μm]
                </div>
                {(() => {
                  const g = getGridParams(
                    groupedPoints.left.length,
                    Math.ceil(groupedPoints.left.length / 2),
                    90,
                  );
                  return (
                    <div
                      className="grid w-fit overflow-visible rounded-[15px] bg-navy-900"
                      style={{
                        gridTemplateColumns: `repeat(${g.subCols}, ${g.subCellWidth}px)`,
                      }}
                    >
                      {groupedPoints.left.map((area, index) => {
                        const totalRows = Math.ceil(
                          groupedPoints.left.length / g.cols,
                        );
                        const currentRow = Math.floor(index / g.cols);
                        const isFirstRow = currentRow === 0;
                        const isLastRow = currentRow === totalRows - 1;
                        const isPartialLastRow =
                          isLastRow && g.itemsInLastRow < g.cols;
                        const span = isPartialLastRow
                          ? g.lastRowSpan
                          : g.normalSpan;
                        const posInRow = index % g.cols;
                        const isFirstInRow = posInRow === 0;
                        const isLastInRow =
                          posInRow === g.cols - 1 ||
                          index === groupedPoints.left.length - 1;
                        const isSelected = selectedPoint === area.nameId;
                        const status = getPaintStatusFromValue(
                          area.value,
                          area.notApplicable,
                        );
                        const config = paintStatusConfig[status];
                        return (
                          <div
                            key={area.nameId}
                            className={clsx(
                              'relative flex cursor-pointer flex-col items-center gap-2.5 px-1 py-4 text-center transition-colors',
                              !isLastInRow && 'border-r',
                              !isLastRow && 'border-b',
                              isSelected && 'bg-blue-900/30',
                              isFirstRow && isFirstInRow && 'rounded-tl-[15px]',
                              isFirstRow && isLastInRow && 'rounded-tr-[15px]',
                              isLastRow && isFirstInRow && 'rounded-bl-[15px]',
                              isLastRow && isLastInRow && 'rounded-br-[15px]',
                            )}
                            style={{
                              borderColor: '#353a6b',
                              gridColumn: `span ${span}`,
                            }}
                            onClick={() => handlePointClick(area.nameId)}
                            onMouseEnter={() => handlePointHover(area.nameId)}
                            onMouseLeave={() => handlePointHover(null)}
                          >
                            {area.isFixed && (
                              <div className="absolute left-1/2 top-[-13px] z-10 -translate-x-1/2 lg:top-[-16px]">
                                <FixedElementBadge disclaimerText="Element naprawiany" />
                              </div>
                            )}
                            <div className="text-base font-medium leading-[14px] text-blue-300">
                              {area.nameId}
                            </div>
                            <div className="flex text-[13px] font-semibold leading-[14px] text-white">
                              {area.displayValue && (
                                <span className="ml-1 text-[11px] font-normal text-blue-300">
                                  ({area.displayValue})
                                </span>
                              )}
                              {area.minMeasurement != null &&
                              area.maxMeasurement != null
                                ? formatMeasurementRange(
                                    area.minMeasurement,
                                    area.maxMeasurement,
                                  )
                                : config.range}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            {SHOW_ENGINE_BAY_VIEW && (
              <div className="flex flex-col items-center">
                <div className="relative top-[-150px]">
                  <VehicleView
                    view="top"
                    vehicleType={vehicleType}
                    points={groupedPoints.top}
                    label=""
                    highlightedPoint={highlightedPoint}
                    onPointHover={handlePointHover}
                    onPointClick={handlePointClick}
                  />
                </div>
                <div className="relative top-[-320px]">
                  <div
                    className="invisible mb-2 mt-8 text-[18px] font-semibold leading-[14px]"
                    aria-hidden="true"
                  >
                    &nbsp;
                  </div>
                  <div
                    className="invisible mb-8 text-[14px] leading-[14px]"
                    aria-hidden="true"
                  >
                    &nbsp;
                  </div>
                  <div className="flex flex-col flex-wrap rounded-[15px] bg-navy-900">
                    {groupedPoints.top.map((area, index) => {
                      const isFirstRow = index === 0;
                      const isLastRow = index === groupedPoints.top.length - 1;
                      const isSelected = selectedPoint === area.nameId;
                      const status = getPaintStatusFromValue(
                        area.value,
                        area.notApplicable,
                      );
                      const config = paintStatusConfig[status];
                      return (
                        <div
                          key={area.nameId}
                          className={clsx(
                            'relative flex cursor-pointer flex-row text-center transition-colors',
                            { 'border-b': !isLastRow },
                            isSelected && 'bg-blue-900/30',
                            isFirstRow && 'rounded-t-[15px]',
                            isLastRow && 'rounded-b-[15px]',
                          )}
                          style={{ borderColor: '#353a6b' }}
                          onClick={() => handlePointClick(area.nameId)}
                          onMouseEnter={() => handlePointHover(area.nameId)}
                          onMouseLeave={() => handlePointHover(null)}
                        >
                          {area.isFixed && (
                            <div className="absolute right-[-10px] top-0 z-10 -translate-y-1/2">
                              <FixedElementBadge disclaimerText="Element naprawiany" />
                            </div>
                          )}
                          <div
                            className="flex w-[44px] items-center justify-center border-r px-1 py-4 text-base font-medium leading-[14px] text-blue-300"
                            style={{ borderColor: '#353a6b' }}
                          >
                            {area.nameId}
                          </div>
                          <div className="flex w-[160px] flex-col items-start px-4 py-4 text-[13px] font-semibold leading-[14px] text-white">
                            {area.displayValue && (
                              <span className="font-archivo text-[12px] font-medium leading-[14px] text-blue-300">
                                {area.displayValue}
                              </span>
                            )}
                            <span className="mt-1 text-[13px] font-semibold leading-[14px] text-white">
                              {area.minMeasurement != null &&
                              area.maxMeasurement != null
                                ? formatMeasurementRange(
                                    area.minMeasurement,
                                    area.maxMeasurement,
                                  )
                                : config.range}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="relative flex flex-col items-center">
              <VehicleView
                view="right"
                vehicleType={vehicleType}
                points={groupedPoints.right}
                label="Strona prawa"
                highlightedPoint={highlightedPoint}
                onPointHover={handlePointHover}
                onPointClick={handlePointClick}
              />
              <div>
                <div
                  className="invisible mb-2 mt-20 text-[18px] font-semibold leading-[14px]"
                  aria-hidden="true"
                >
                  &nbsp;
                </div>
                <div
                  className="relative top-[-190px] hidden min-[1600px]:block"
                  style={{
                    left:
                      vehicleType === 'hatchback'
                        ? '100px'
                        : vehicleType === 'sedan'
                          ? 140
                          : '150px',
                  }}
                >
                  <PaintThicknessLegend />
                </div>

                <div className="absolute bottom-[150px] right-0 hidden lg:block min-[1600px]:hidden">
                  {/* TODO: wneki silnika */}
                  {/* bottom-[290px] */}
                  <PaintThicknessLegend variant="mobile" />
                </div>

                <div
                  className="invisible mb-8 text-[14px] leading-[14px]"
                  aria-hidden="true"
                >
                  &nbsp;
                </div>
                {(() => {
                  const g = getGridParams(
                    groupedPoints.right.length,
                    Math.ceil(groupedPoints.right.length / 2),
                    90,
                  );
                  return (
                    <div
                      className="grid w-fit overflow-visible rounded-[15px] bg-navy-900"
                      style={{
                        gridTemplateColumns: `repeat(${g.subCols}, ${g.subCellWidth}px)`,
                      }}
                    >
                      {groupedPoints.right.map((area, index) => {
                        const totalRows = Math.ceil(
                          groupedPoints.right.length / g.cols,
                        );
                        const currentRow = Math.floor(index / g.cols);
                        const isFirstRow = currentRow === 0;
                        const isLastRow = currentRow === totalRows - 1;
                        const isPartialLastRow =
                          isLastRow && g.itemsInLastRow < g.cols;
                        const span = isPartialLastRow
                          ? g.lastRowSpan
                          : g.normalSpan;
                        const posInRow = index % g.cols;
                        const isFirstInRow = posInRow === 0;
                        const isLastInRow =
                          posInRow === g.cols - 1 ||
                          index === groupedPoints.right.length - 1;
                        const isSelected = selectedPoint === area.nameId;
                        const status = getPaintStatusFromValue(
                          area.value,
                          area.notApplicable,
                        );
                        const config = paintStatusConfig[status];
                        return (
                          <div
                            key={area.nameId}
                            className={clsx(
                              'relative flex cursor-pointer flex-col items-center gap-2.5 px-1 py-4 text-center transition-colors',
                              !isLastInRow && 'border-r',
                              !isLastRow && 'border-b',
                              isSelected && 'bg-blue-900/30',
                              isFirstRow && isFirstInRow && 'rounded-tl-[15px]',
                              isFirstRow && isLastInRow && 'rounded-tr-[15px]',
                              isLastRow && isFirstInRow && 'rounded-bl-[15px]',
                              isLastRow && isLastInRow && 'rounded-br-[15px]',
                            )}
                            style={{
                              borderColor: '#353a6b',
                              gridColumn: `span ${span}`,
                            }}
                            onClick={() => handlePointClick(area.nameId)}
                            onMouseEnter={() => handlePointHover(area.nameId)}
                            onMouseLeave={() => handlePointHover(null)}
                          >
                            {area.isFixed && (
                              <div className="absolute left-1/2 top-[-13px] z-10 -translate-x-1/2 lg:top-[-16px]">
                                <FixedElementBadge disclaimerText="Element naprawiany" />
                              </div>
                            )}
                            <div className="text-base font-medium leading-[14px] text-blue-300">
                              {area.nameId}
                            </div>
                            <div className="text-[13px] font-semibold leading-[14px] text-white">
                              {area.minMeasurement != null &&
                              area.maxMeasurement != null
                                ? formatMeasurementRange(
                                    area.minMeasurement,
                                    area.maxMeasurement,
                                  )
                                : config.range}
                              {area.displayValue && (
                                <span className="ml-1 text-[11px] font-normal text-blue-300">
                                  ({area.displayValue})
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Mobile: Swiper slider */}
          <div className="relative z-10 -mb-8 w-full flex-1 pb-8 lg:mb-0 lg:hidden lg:pb-8">
            <div className="relative z-20 block px-4 lg:hidden">
              <PaintThicknessLegend variant="mobile" />
            </div>
            {/* Left edge gradient */}
            <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-[89px] bg-gradient-to-r from-[#11153b]/90 to-[#11153b]/0" />
            {/* Right edge gradient */}
            <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-[89px] bg-gradient-to-l from-[#11153b]/90 to-[#11153b]/0" />
            <Swiper
              slidesPerView={1.1}
              spaceBetween={20}
              centeredSlides={true}
              autoHeight
              className="w-full !overflow-x-clip !overflow-y-visible"
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              onSlideChange={(swiper) => {
                setActiveSlide(swiper.activeIndex);
              }}
            >
              <SwiperSlide>
                <VehicleView
                  view="left"
                  vehicleType={vehicleType}
                  points={groupedPoints.left}
                  label="Strona lewa"
                  highlightedPoint={highlightedPoint}
                  onPointHover={handlePointHover}
                  onPointClick={handlePointClick}
                  onVehicleSvgLoad={updateSwiperLayout}
                />
                <div className="mb-4 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary lakieru wnęk
                </div>
                <div className="font-regular mb-8 text-center text-[14px] leading-[14px] text-blue-300">
                  Wartość zmierzona w [μm]
                </div>
                <MeasurementsMobileGrid
                  items={groupedPoints.left.map(
                    (area): MeasurementsMobileGridItem => ({
                      id: area.nameId,
                      label: area.nameId,
                      value:
                        area.minMeasurement != null &&
                        area.maxMeasurement != null
                          ? formatMeasurementRange(
                              area.minMeasurement,
                              area.maxMeasurement,
                            )
                          : paintStatusConfig[
                              getPaintStatusFromValue(
                                area.value,
                                area.notApplicable,
                              )
                            ].range,
                      isFixed: area.isFixed,
                    }),
                  )}
                  selectedItemId={selectedPoint}
                  onItemClick={handlePointClick}
                  onItemHover={handlePointHover}
                />
              </SwiperSlide>

              {SHOW_ENGINE_BAY_VIEW && (
                <SwiperSlide>
                  <div className="px-[40px]">
                    <VehicleView
                      view="top"
                      vehicleType={vehicleType}
                      heightMobile={topViewHeightMobile}
                      points={groupedPoints.top}
                      label=""
                      highlightedPoint={highlightedPoint}
                      onPointHover={handlePointHover}
                      onPointClick={handlePointClick}
                      onVehicleSvgLoad={updateSwiperLayout}
                    />
                  </div>
                  <div className="mb-4 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                    Pomiary komory silnika
                  </div>
                  <div className="font-regular mb-8 text-center text-[14px] leading-[14px] text-blue-300">
                    Wartość zmierzona w [μm]
                  </div>
                  <MeasurementsMobileGrid
                    items={groupedPoints.top.map(
                      (area): MeasurementsMobileGridItem => ({
                        id: area.nameId,
                        label: area.nameId,
                        value:
                          area.minMeasurement != null &&
                          area.maxMeasurement != null
                            ? formatMeasurementRange(
                                area.minMeasurement,
                                area.maxMeasurement,
                              )
                            : paintStatusConfig[
                                getPaintStatusFromValue(
                                  area.value,
                                  area.notApplicable,
                                )
                              ].range,
                        isFixed: area.isFixed,
                      }),
                    )}
                    selectedItemId={selectedPoint}
                    onItemClick={handlePointClick}
                    onItemHover={handlePointHover}
                  />
                </SwiperSlide>
              )}

              <SwiperSlide>
                <VehicleView
                  view="right"
                  vehicleType={vehicleType}
                  points={groupedPoints.right}
                  label="Strona prawa"
                  highlightedPoint={highlightedPoint}
                  onPointHover={handlePointHover}
                  onPointClick={handlePointClick}
                  onVehicleSvgLoad={updateSwiperLayout}
                />
                <div className="mb-4 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary lakieru wnęk
                </div>
                <div className="font-regular mb-8 text-center text-[14px] leading-[14px] text-blue-300">
                  Wartość zmierzona w [μm]
                </div>
                <MeasurementsMobileGrid
                  items={groupedPoints.right.map(
                    (area): MeasurementsMobileGridItem => ({
                      id: area.nameId,
                      label: area.nameId,
                      value:
                        area.minMeasurement != null &&
                        area.maxMeasurement != null
                          ? formatMeasurementRange(
                              area.minMeasurement,
                              area.maxMeasurement,
                            )
                          : paintStatusConfig[
                              getPaintStatusFromValue(
                                area.value,
                                area.notApplicable,
                              )
                            ].range,
                      isFixed: area.isFixed,
                    }),
                  )}
                  selectedItemId={selectedPoint}
                  onItemClick={handlePointClick}
                  onItemHover={handlePointHover}
                />
              </SwiperSlide>
            </Swiper>

            <div
              aria-hidden="true"
              aria-label="Nawigacja widoków pojazdu"
              data-testid="paint-points-inline-slider-navigation"
              role="navigation"
              className="pointer-events-none invisible lg:hidden"
            >
              <SliderNavigation
                activeIndex={activeSlide}
                visibleSlides={SHOW_ENGINE_BAY_VIEW ? undefined : [0, 2]}
                onNavigate={(index) => {
                  swiperRef.current?.slideTo(index);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    // {/* Legenda z pełnym gradientem kolorów */ }
    // <div className="px-4 mt-4">
    //   <div className="flex flex-wrap gap-3 justify-start items-center">
    //     {Object.entries(paintStatusConfig)
    //       .filter(([status]) => status !== 'MISSING') // Pomijamy MISSING w legendzie
    //       .map(([status, config]) => (
    //         <div key={status} className="flex gap-2 items-center">
    //           <div
    //             className="w-5 h-5 rounded border"
    //             style={{
    //               backgroundColor: config.fillColor,
    //               borderColor: config.strokeColor,
    //             }}
    //           />
    //           <span className="text-xs font-medium text-text-primary">
    //             {config.range}
    //           </span>
    //         </div>
    //       ))}
    //   </div>
    // </div>

    // {/* Tabela szczegółów punktów */ }
    // <div className="overflow-hidden mt-8 bg-white rounded-lg border border-grayBorder">
    //   <div className="px-6 py-4 bg-gray-100 border-b border-grayBorder">
    //     <h3 className="text-base font-semibold text-text-primary">
    //       Pomiary lakieru wnęk oraz komory silnika
    //     </h3>
    //     <p className="mt-1 text-sm text-gray-600">
    //       Wartość zmierzona w [μm]
    //     </p>
    //   </div>
    //   <div className="p-6">
    //     <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
    //       {[...groupedPoints.left, ...groupedPoints.top, ...groupedPoints.right]
    //         .sort(
    //           (a, b) =>
    //             parseInt(String(a.pointNumber)) - parseInt(String(b.pointNumber)),
    //         )
    //         .map((point) => {
    //           const value = point.value;
    //           const displayValue = value !== undefined ? `${value}` : 'BRAK';
    //           const status = getPaintStatusFromValue(value);

    //           // Pobierz konfigurację koloru dla statusu
    //           const config = paintStatusConfig[status];

    //           return (
    //             <div
    //               key={`point-${point.pointNumber}`}
    //               className="flex flex-col p-3 bg-gray-50 rounded-lg border border-gray-200"
    //             >
    //               <div className="flex justify-between items-center mb-2">
    //                 <span className="text-xs font-bold text-gray-500">
    //                   {point.pointNumber}
    //                 </span>
    //               </div>
    //               <div className="text-sm font-semibold text-text-primary">
    //                 {displayValue}
    //               </div>
    //               <div className="mt-1 text-xs text-gray-500">
    //                 {config.range}
    //               </div>
    //             </div>
    //           );
    //         })}
    //     </div>
    //   </div>
    // </div>
  );
};
